// Command Center, Strip Board layout (POC). The brief is filed as ruled strips
// in three bays — Fires, Today, Watch — and acting on a strip moves it.
// Classic layout: CommandCenter.jsx. Toggle: CommandCenterSwitch.jsx. Data and
// actions: useMorningBrief.js (shared with the Riso layout).
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlarmClock, ArrowRight, Bot, CheckCircle2, ChevronDown, Compass, Inbox, NotebookPen, Plug, Undo2 } from 'lucide-react';
import { entityPath } from '../lib/productInsights';
import { BrainStatus } from '../components/brain/BrainUI';
import WorkspaceInsights from '../components/brain/WorkspaceInsights';
import { AREA_OF_KIND, AREA_OF_TYPE, clock, humanDates, safeName, sinceLabel, useMorningBrief, verb } from './useMorningBrief';
import './CommandCenterStrips.css';


// Brief ids are "<kind>:<record id>"; the kind decides record type and label.
// Color is urgency only (see urgencyOf in useMorningBrief.js), never record type.
const KINDS = {
  issue: { type: 'issues', label: 'Critical issue', tag: 'Issue · T1' },
  readiness: { type: 'initiatives', label: 'Initiative', tag: 'Initiative' },
  validated: { type: 'problems', label: 'Problem', tag: 'Problem' },
  stronger: { type: 'problems', label: 'Problem', tag: 'Problem' },
  stale: { type: 'problems', label: 'Stale evidence', tag: 'Problem · stale' },
  decision: { type: 'decisions', label: 'Decision review', tag: 'Decision' },
  experiment: { type: 'experiments', label: 'Experiment', tag: 'Experiment' },
};


function Strip({ urgency, item, kind, meta, status, isNew, next, first, onAct, onSnooze, onDismiss, onUndo, onWake, busy }) {
  const until = status.snoozedUntil && new Date(status.snoozedUntil);
  return <article tabIndex={-1} data-strip={item.id} className={`ccs-strip area-${AREA_OF_KIND[kind.prefix] || 'next'}${next ? ' is-next' : ''}${status.stamped ? ' is-stamped' : ''}${until ? ' is-snoozed' : ''}`} data-urgency={until ? 'normal' : urgency} style={{ viewTransitionName: safeName(item.id) }} data-guide={first ? 'brief-first' : undefined} aria-label={`${kind.label}: ${item.title}`}>
    <span className="ccs-tab" aria-hidden="true" />
    <div className="ccs-type">
      <span className="ccs-tag">{next ? 'Next' : kind.tag}</span>
      {!next && <span className="ccs-meta">{until ? `Until ${until.toLocaleDateString(undefined, { weekday: 'short' })} ${until.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}` : meta}</span>}
      {isNew && <span className="ccs-new" title="Not in your previous brief"><span className="v2-sr">New to brief</span></span>}
    </div>
    <div className="ccs-main">
      {next && <span className="ccs-tag ccs-tag-line">{[kind.tag, meta].filter(Boolean).join(' · ')}</span>}
      <h3 title={item.title}>{item.title}</h3>
      <p title={humanDates(item.detail)}>{humanDates(item.detail)}</p>
    </div>
    <div className="ccs-actions">
      {status.stamped
        ? <><span className="ccs-stamp" role="status">Dismissed</span><button type="button" className="ccs-quiet" onClick={onUndo} disabled={!status.recordId}><Undo2 aria-hidden="true" />Undo</button></>
        : <>
          <Link className={next ? 'v2-button' : 'ccs-go'} to={item.href} onClick={onAct} aria-label={item.action} data-follow>{next ? item.action : verb(item.action)}<ArrowRight aria-hidden="true" /></Link>
          {until
            ? <button type="button" className="ccs-quiet" onClick={onWake} disabled={busy}>Wake now</button>
            : <><button type="button" className="ccs-quiet" onClick={onSnooze} disabled={busy}><AlarmClock aria-hidden="true" />Snooze</button><button type="button" className="ccs-quiet" onClick={onDismiss} disabled={busy}>Dismiss</button></>}
        </>}
    </div>
  </article>;
}

function Bay({ id, title, count, hint, tone, children, empty }) {
  return <section id={`ccs-bay-${id}`} className={`ccs-bay${tone ? ` ccs-bay-${tone}` : ''}`} aria-labelledby={`ccs-${id}`}>
    <header className="ccs-bay-head"><h2 id={`ccs-${id}`}>{title}</h2><span className="ccs-count">{count}</span><span className="ccs-rule" aria-hidden="true" />{hint && <span className="ccs-hint">{hint}</span>}</header>
    {count ? <div className="ccs-strips">{children}</div> : empty}
  </section>;
}

function ArrivalsList({ arrivals, loading, now }) {
  if (!arrivals.length) return <p className="ccs-quiet-note">{loading ? 'Loading changes…' : 'No records changed since then.'}</p>;
  return <ol className="ccs-arrival-list">{arrivals.map(n => <li key={`${n.type}:${n.id}`} className={`area-${AREA_OF_TYPE[n.type] || 'next'}`}>
    <Link to={entityPath(n.type, n.id)}><i aria-hidden="true" /><span><strong>{n.label}</strong><small>{n.change}</small></span><time dateTime={n.at}>{clock(n.at, now)}</time></Link>
  </li>)}</ol>;
}

export default function CommandCenterStrips({ layoutSwitch = null }) {
  const brief = useMorningBrief({ kinds: KINDS, movingClass: 'ccs-moving' });
  const { data, loading, workspace, guide, now, baseline, error, busy, strips, fires, today, watch, clearedToday, firstId, arrivals, record, remove, onKeyDown } = brief;
  const [showAll, setShowAll] = useState(false);
  const [entered, setEntered] = useState(false);
  const boardRef = useRef(null);
  useEffect(() => { if (!loading && !entered) { const t = setTimeout(() => setEntered(true), 700); return () => clearTimeout(t); } }, [loading, entered]);
  const visibleToday = showAll ? today : today.slice(0, 6);

  const renderStrip = (s, next = false) => <Strip key={s.item.id} {...s} next={next} first={s.item.id === firstId} busy={busy.has(s.item.id)}
    onAct={() => guide?.act(s.item.id)}
    onSnooze={() => record(s.item, 'snooze')}
    onDismiss={() => record(s.item, 'dismiss')}
    onUndo={() => remove(s.item, [s.status.recordId])}
    onWake={() => remove(s.item, s.status.records.map(f => f.id))} />;

  const jump = id => document.getElementById(`ccs-bay-${id}`)?.scrollIntoView({ block: 'start', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  const skeleton = <div className="ccs-strips" aria-hidden="true">{[0, 1, 2].map(i => <div className="ccs-strip ccs-skeleton" key={i} />)}</div>;
  const noBrief = !loading && !strips.length;
  const changesTitle = baseline.at ? 'Since you were here' : 'Recently updated';
  const changesWhen = baseline.at ? sinceLabel(baseline.at) : 'Your latest product record changes';

  return <div className="v2-page ccs">
    <header className="ccs-head">
      <div>
        <h1>Your morning brief</h1>
        <p>{[workspace?.name, now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })].filter(Boolean).join(' · ')}</p>
      </div>
      <div className="ccs-head-tools">
        <div className="ccs-tally" role="group" aria-label="Brief totals">
          <button type="button" className={fires.length ? 'is-hot' : ''} onClick={() => jump('fires')}><span>Fires</span><strong>{loading ? '—' : fires.length}</strong></button>
          <button type="button" onClick={() => jump('today')}><span>Today</span><strong>{loading ? '—' : today.length}</strong></button>
          <button type="button" onClick={() => jump('watch')}><span>Watch</span><strong>{loading ? '—' : watch.length}</strong></button>
          <div><span>Cleared today</span><strong>{loading ? '—' : clearedToday}</strong></div>
        </div>
        {layoutSwitch}
        <Link className="v2-button" to="/inbox" data-guide="capture-signal"><Inbox aria-hidden="true" />Capture a signal</Link>
      </div>
    </header>
    <BrainStatus quietRefresh />
    {error && <div className="v2-error ccs-error" role="alert">{error}</div>}

    <div className="ccs-board">
      <div className={`ccs-bays${entered ? '' : ' is-entering'}`} ref={boardRef} onKeyDown={onKeyDown}>
        {loading ? skeleton : noBrief
          ? <div className="v2-empty"><h2>{data.problems.length ? 'Nothing urgent in your saved records' : 'Start with a customer problem'}</h2><p>Capture a signal in Inbox, or add a problem in Discovery. Your brief will reflect the evidence and initiatives you save.</p></div>
          : <>
            <Bay id="fires" title="Fires" tone="fire" count={fires.length} hint="Tier 1 issues" empty={<p className="ccs-clear"><CheckCircle2 aria-hidden="true" />No critical issues open.</p>}>{fires.map(s => renderStrip(s))}</Bay>
            <details className="ccs-changes-compact">
              <summary><span className="ccs-dot" aria-hidden="true" /><strong>{arrivals.length} {arrivals.length === 1 ? 'change' : 'changes'}</strong> {baseline.at ? `since ${sinceLabel(baseline.at)}` : 'recently'}<ChevronDown aria-hidden="true" /></summary>
              <ArrivalsList arrivals={arrivals} loading={loading} now={now} />
            </details>
            <Bay id="today" title="Today" count={today.length} hint="Calculated from your records" empty={<p className="ccs-clear"><CheckCircle2 aria-hidden="true" />Nothing else needs a decision today.</p>}>
              {visibleToday.map((s, i) => renderStrip(s, i === 0))}
              {today.length > visibleToday.length && <button type="button" className="ccs-more" onClick={() => setShowAll(true)}>Show {today.length - visibleToday.length} more</button>}
            </Bay>
            <Bay id="watch" title="Watch" tone="watch" count={watch.length} hint="Stale evidence and snoozed prompts" empty={<p className="ccs-clear">Snoozed prompts wait here until they wake.</p>}>{watch.map(s => renderStrip(s))}</Bay>
            <p className="ccs-keys" aria-hidden="true"><kbd>J</kbd><kbd>K</kbd> move <kbd>Enter</kbd> open <kbd>S</kbd> snooze <kbd>E</kbd> dismiss <kbd>Z</kbd> undo</p>
          </>}
      </div>

      <aside className="ccs-side">
        <section className="ccs-arrivals" aria-labelledby="ccs-arrivals">
          <h2 id="ccs-arrivals">{changesTitle}</h2>
          <p className="ccs-since">{changesWhen}</p>
          <ArrivalsList arrivals={arrivals} loading={loading} now={now} />
        </section>
        <nav className="ccs-jump" aria-label="Shortcuts">
          {guide?.activation?.path === 'jira' && !data.imports.length && <Link to="/onboarding"><Plug aria-hidden="true" />Setup &amp; integrations</Link>}
          <Link to="/autopilot"><Bot aria-hidden="true" />Agent team</Link>
          <Link to="/brain"><Compass aria-hidden="true" />Product Brain</Link>
          <Link to="/decisions"><NotebookPen aria-hidden="true" />Record a decision</Link>
          <Link to="/learning"><CheckCircle2 aria-hidden="true" />Review learning</Link>
        </nav>
        <WorkspaceInsights area="command_center" />
      </aside>
    </div>
  </div>;
}
